import { useContext, useEffect, useState } from "react";
import { PlanType } from "modules/Const";
import useScreen from "Hooks/useScreen";
import useBrand from "Hooks/useBrand";
import SubscriptionDetails from "components/SubscriptionDetails";
import ModalSymphony from "elements/ModalSymphony";
import { clsx } from "modules/Utils";
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";

interface UpgradeToProModalProps {
    /** Optional class name for styling */
    className?: string;
    /** Controls modal visibility */
    isOpen: boolean;
    /** Callback when modal is closed */
    onClose: () => void;
    /** Optional array to restrict which plans are shown */
    onlyShowPlans?: PlanType[];
    /** Optional custom call to action text */
    upgradeCallToAction?: string;
    /** Optional plan to highlight */
    highlightedPlan?: PlanType;
    /** Source identifier for tracking where the upgrade was initiated from */
    checkout_source?: string;
    /** Whether this is a downgrade flow */
    isDowngrade?: boolean;
    /** URL to redirect to after the plan upgrade is successful */
    successUrl?: string;
    /** URL to redirect to after the plan upgrade is cancelled */
    cancelUrl?: string;
}

const UpgradeToProModal = ({
    isOpen,
    className,
    onClose,
    onlyShowPlans,
    upgradeCallToAction,
    highlightedPlan,
    checkout_source,
    isDowngrade = false,
    successUrl,
    cancelUrl
}: UpgradeToProModalProps) => {
    const { mobileView } = useScreen();
    const { currentBrand, isGrandfathered } = useBrand(); // Fetch isGrandfathered

    const {
        setShowPlanUpgradeSuccessModal,
        showPlanUpgradeSuccessModal,
    } = useContext(UpgradeModalContext)

    // Only show current supported plans (deprecated plans LITE, PRO, TEAM are no longer available)
    // All users now see the same modern plans regardless of grandfathered status
    const defaultPlans = [PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2];

    const [planTabs, setPlanTabs] = useState<PlanType[]>(
        onlyShowPlans && onlyShowPlans.length > 0 ? onlyShowPlans : defaultPlans
    );

    // Update available plans when onlyShowPlans or isGrandfathered changes
    useEffect(() => {
        setPlanTabs(onlyShowPlans && onlyShowPlans.length > 0 ? onlyShowPlans : defaultPlans);
    }, [onlyShowPlans, isGrandfathered]); // Use isGrandfathered instead of defaultPlans

    /** Handler for modal close button click */
    const handleClickCloseIconButton = () => {
        if (onClose) onClose();
    };

    const handlePlanChangedSuccess = async (tier: PlanType) => {
        setShowPlanUpgradeSuccessModal(true);
    };

    /** Reduce modal width if only showing one plan option */
    const makeSmallerWidth = onlyShowPlans && onlyShowPlans.length === 1;

    const getModalWidth = () => {
        if (onlyShowPlans && onlyShowPlans.length === 3) {
            return 1050;
        }

        return 750;
    }

    return (
        <ModalSymphony
            className={clsx("modalSymphonyUpgradeToProModal", className)}
            isOpen={isOpen && !showPlanUpgradeSuccessModal}
            onClose={handleClickCloseIconButton}
            fullScreen={mobileView}
            size="large"
            maxWidth={getModalWidth()}
        >
            <SubscriptionDetails
                onPlanChangedSuccess={handlePlanChangedSuccess}
                checkout_source={checkout_source}
                onlyShowPlans={planTabs}
                highlightedPlan={highlightedPlan || undefined}
                upgradeCallToAction={upgradeCallToAction || undefined}
                isDowngrade={isDowngrade}
                className="subscriptionDetailsUpgradeToProModal"
                successUrl={successUrl}
                cancelUrl={cancelUrl}
                isGrandfathered={isGrandfathered} // Use actual grandfathered status from useBrand()
                onModalClose={onClose}
            />
        </ModalSymphony>
    );
};

export default UpgradeToProModal;
